import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  hasPermission,
  privilegesEnum,
} from "../../../helpers/PermissionHelper";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../../helpers/FilterSearchHelper";
import {
  getTablePagination,
  DEFAULT_TABLE_PAGE_SIZE,
} from "../../../helpers/TablePaginationHelper";
import { COMMERCIAL_INTELLIGENCE_PATHS } from "../../../helpers/MenuPathHelper";

import {
  Alert,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Button,
  Skeleton,
  Tabs,
  Table,
  Empty,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";

import styles from "./ViewCampaign.module.scss";
import CustomBreadcrumb from "../../../components/breadcrumb/Breadcrumb";
import {
  updateCampaign,
  getCampaignById,
  getCampaignClientSegmentIds,
  getClientSegmentsFilteredLite,
  getPromotions,
  getRewards,
  getCampaignClients,
} from "../../../api/Services";
import moment from "moment";
import NotFound from "../../notfound/NotFound";
import { RxMagicWand } from "react-icons/rx";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const CLIENTS_SKELETON_CELL_WIDTHS = ["120px", "140px", "120px", "180px", "110px"];

const CampaignClientsTableSkeleton = ({ rows }: { rows: number }) => (
  <div className={styles.tableSkeleton}>
    <div className={styles.tableSkeletonHeader}>
      {CLIENTS_SKELETON_CELL_WIDTHS.map((width, index) => (
        <div key={`header-${index}`} className={styles.tableSkeletonCell}>
          <Skeleton.Input active size="small" style={{ width, height: 14 }} />
        </div>
      ))}
    </div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={`row-${rowIndex}`} className={styles.tableSkeletonRow}>
        {CLIENTS_SKELETON_CELL_WIDTHS.map((width, cellIndex) => (
          <div
            key={`cell-${rowIndex}-${cellIndex}`}
            className={styles.tableSkeletonCell}
          >
            <Skeleton.Input
              active
              size="small"
              style={{ width, height: 16 }}
            />
          </div>
        ))}
      </div>
    ))}
  </div>
);

const ViewCampaign = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [campaign, setCampaign] = useState<any>();
  const [clientSegments, setClientSegments] = useState<any[]>([]);
  const [promotions, setPromotions] = useState<any[]>([]);
  const [rewards, setRewards] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState("detail");
  const [clientsTabOpened, setClientsTabOpened] = useState(false);

  const [clients, setClients] = useState<any[]>([]);
  const [clientsTotal, setClientsTotal] = useState(0);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsPage, setClientsPage] = useState(1);
  const [clientsPageSize, setClientsPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [nameFilterValue, setNameFilterValue] = useState("");
  const [emailFilterValue, setEmailFilterValue] = useState("");
  const [phoneFilterValue, setPhoneFilterValue] = useState("");
  const [idNumberFilterValue, setIdNumberFilterValue] = useState("");
  const debouncedNameFilter = useDebouncedValue(nameFilterValue);
  const debouncedEmailFilter = useDebouncedValue(emailFilterValue);
  const debouncedPhoneFilter = useDebouncedValue(phoneFilterValue);
  const debouncedIdNumberFilter = useDebouncedValue(idNumberFilterValue);
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();

  const [editForm] = Form.useForm();

  const canEdit = hasPermission(privilegesEnum.WRITE_CAMPAIGNS);
  const canReadClients = hasPermission(privilegesEnum.READ_CLIENTS);
  const isCreating = campaign?.campaignStatus === "CREANDO";

  const { id } = useParams();

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/campaigns";
    }
    return "/promotions/campaigns";
  };

  const getClientsBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/clients";
    }
    return "/promotions/clients";
  };

  const getBackUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const queryString = searchParams.toString();
    return `${getBasePath()}${queryString ? `?${queryString}` : ""}`;
  };

  const applyCampaignToForm = useCallback(
    (response: any) => {
      if (response.promotion) {
        setPromotions((prev) => {
          const exists = prev.some((p) => p.id === response.promotion.id);
          return exists ? prev : [response.promotion, ...prev];
        });
      }
      if (response.reward) {
        setRewards((prev) => {
          const exists = prev.some((r) => r.id === response.reward.id);
          return exists ? prev : [response.reward, ...prev];
        });
      }
      editForm.setFieldsValue({
        name: response.name,
        description: response.description || undefined,
        campaignType: response.campaignType,
        campaignStatus: response.campaignStatus,
        estimatedImpact: response.estimatedImpact,
        estimatedCost: response.estimatedCost,
        estimatedRoi: response.estimatedRoi,
        estimatedAudience: response.estimatedAudience,
        validity:
          response.validityStartDate && response.validityEndDate
            ? [
                moment(response.validityStartDate),
                moment(response.validityEndDate),
              ]
            : null,
        promotionId: response.promotion?.id,
        rewardId: response.reward?.id,
      });
    },
    [editForm]
  );

  const handleFetchSegments = useCallback(async () => {
    if (!id) return;
    setSegmentsLoading(true);
    try {
      const segments = await getCampaignClientSegmentIds(id);
      if (segments?.length) {
        setClientSegments((prev) => {
          const byId = new Map(prev.map((s: any) => [s.id, s]));
          segments.forEach((s: { id: number; name: string }) =>
            byId.set(s.id, { id: s.id, name: s.name })
          );
          return Array.from(byId.values());
        });
        editForm.setFieldsValue({
          clientSegmentIds: segments.map((s) => s.id),
        });
      }
    } finally {
      setSegmentsLoading(false);
    }
  }, [id, editForm]);

  const handleFetchCampaign = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const response = await getCampaignById(id);
    if (response) {
      setCampaign(response);
      applyCampaignToForm(response);
      setLoading(false);
      handleFetchSegments();
    } else {
      setCampaign(null);
      setLoading(false);
    }
  }, [id, applyCampaignToForm, handleFetchSegments]);

  const handlePollCampaignStatus = useCallback(async () => {
    if (!id) return;
    const response = await getCampaignById(id);
    if (response) {
      setCampaign(response);
      editForm.setFieldsValue({ campaignStatus: response.campaignStatus });
    }
  }, [id, editForm]);

  const handleSearchClientSegments = useCallback(async (value: string) => {
    const response = await getClientSegmentsFilteredLite(1, { name: value });
    setClientSegments((prev) => {
      const byId = new Map(prev.map((s: any) => [s.id, s]));
      (response?.clientSegments || []).forEach((s: any) => byId.set(s.id, s));
      return Array.from(byId.values());
    });
  }, []);

  const handleSearchPromotions = useCallback(async (value: string) => {
    const response = await getPromotions(1, { name: value });
    setPromotions(response.promotions || []);
  }, []);

  const handleSearchRewards = useCallback(async (value: string) => {
    const response = await getRewards(1, { name: value });
    setRewards(response.rewards || []);
  }, []);

  const handlePromotionChange = (value: any) => {
    if (value) {
      editForm.setFieldsValue({ rewardId: undefined });
    }
  };

  const handleRewardChange = (value: any) => {
    if (value) {
      editForm.setFieldsValue({ promotionId: undefined });
    }
  };

  const handleCreateNewSegment = () => {
    const currentPath = location.pathname;
    const currentFormValues = editForm.getFieldsValue();
    navigate(`${COMMERCIAL_INTELLIGENCE_PATHS.segments}/create`, {
      state: {
        returnToCampaign: currentPath,
        campaignFormData: currentFormValues,
      },
    });
  };

  const fetchClients = useCallback(async () => {
    if (!id || !clientsTabOpened) return;
    const requestId = beginRequest();
    setClientsLoading(true);
    try {
      const response = await getCampaignClients(id, clientsPage, {
        size: clientsPageSize,
        name: debouncedNameFilter || undefined,
        email: debouncedEmailFilter || undefined,
        phone: debouncedPhoneFilter || undefined,
        idNumber: debouncedIdNumberFilter || undefined,
      });
      if (isStaleRequest(requestId)) return;
      setClients(response.clients || []);
      setClientsTotal(response.totalElements || 0);
    } finally {
      if (!isStaleRequest(requestId)) {
        setClientsLoading(false);
      }
    }
  }, [
    id,
    clientsTabOpened,
    clientsPage,
    clientsPageSize,
    debouncedNameFilter,
    debouncedEmailFilter,
    debouncedPhoneFilter,
    debouncedIdNumberFilter,
    beginRequest,
    isStaleRequest,
  ]);

  useEffect(() => {
    handleFetchCampaign();
    handleSearchClientSegments("");
    handleSearchPromotions("");
    handleSearchRewards("");
  }, [
    handleFetchCampaign,
    handleSearchClientSegments,
    handleSearchPromotions,
    handleSearchRewards,
  ]);

  useEffect(() => {
    if (!isCreating) return;
    const interval = setInterval(() => handlePollCampaignStatus(), 5000);
    return () => clearInterval(interval);
  }, [isCreating, handlePollCampaignStatus]);

  useEffect(() => {
    const state = location.state as
      | { newSegmentId?: number; campaignFormData?: any }
      | null;
    const newSegmentId = state?.newSegmentId;
    const savedFormData = state?.campaignFormData;

    if (newSegmentId || savedFormData) {
      if (savedFormData) {
        editForm.setFieldsValue(savedFormData);
      }

      if (newSegmentId) {
        handleSearchClientSegments("").then(() => {
          const currentSegmentIds =
            editForm.getFieldValue("clientSegmentIds") || [];
          if (!currentSegmentIds.includes(newSegmentId)) {
            editForm.setFieldsValue({
              clientSegmentIds: [...currentSegmentIds, newSegmentId],
            });
          }
        });
      }

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    if (!clientsTabOpened) return;
    setClientsPage(1);
  }, [
    debouncedNameFilter,
    debouncedEmailFilter,
    debouncedPhoneFilter,
    debouncedIdNumberFilter,
    clientsTabOpened,
  ]);

  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const handleEdit = async () => {
    if (!id) return;

    await editForm.validateFields().then(async (values) => {
      setSaving(true);
      const {
        name,
        description,
        campaignType,
        campaignStatus,
        estimatedImpact,
        estimatedCost,
        estimatedRoi,
        estimatedAudience,
        validity,
        promotionId,
        rewardId,
        clientSegmentIds,
      } = values;

      if (promotionId && rewardId) {
        message.error(t("Common-CampaignPromotionOrRewardError"));
        setSaving(false);
        return;
      }

      const validityStartDate = validity
        ? validity[0]?.format("YYYY-MM-DD")
        : null;
      const validityEndDate = validity
        ? validity[1]?.format("YYYY-MM-DD")
        : null;

      let response = await updateCampaign(id, {
        name,
        description:
          description && description !== "null" && description.trim() !== ""
            ? description
            : undefined,
        validityStartDate,
        validityEndDate,
        promotionId: promotionId || undefined,
        rewardId: rewardId || undefined,
        clientSegmentIds: clientSegmentIds || [],
        campaignType,
        campaignStatus,
        estimatedImpact,
        estimatedCost,
        estimatedRoi,
        estimatedAudience,
      });

      if (response) {
        message.success(t("Common-UpdateSuccessMessage"));
        await handleFetchCampaign();
      } else {
        message.error(t("Common-ErrorMessage"));
      }

      setSaving(false);
    });
  };

  const handleCancelButton = () => {
    navigate(getBackUrl());
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === "clients" && !clientsTabOpened) {
      setClientsLoading(true);
      setClientsTabOpened(true);
    }
  };

  const hasActiveClientFilters = () =>
    nameFilterValue !== "" ||
    emailFilterValue !== "" ||
    phoneFilterValue !== "" ||
    idNumberFilterValue !== "";

  const handleClearClientFilters = () => {
    setNameFilterValue("");
    setEmailFilterValue("");
    setPhoneFilterValue("");
    setIdNumberFilterValue("");
    setClientsPage(1);
  };

  const clientColumns = useMemo(
    () => [
      {
        title: t("Common-Identifier"),
        key: "identifier",
        ellipsis: true,
        render: (_: any, record: any) => {
          const externalId = record.externalId ?? record.external_id;
          const idNumber = record.idNumber;
          const main =
            (idNumber != null && String(idNumber).trim() !== ""
              ? String(idNumber)
              : null) ||
            (externalId != null && String(externalId).trim() !== ""
              ? String(externalId)
              : null) ||
            "-";
          return canReadClients ? (
            <a href={`${getClientsBasePath()}/${record.id}/view`}>{main}</a>
          ) : (
            main
          );
        },
      },
      {
        title: t("Common-Name"),
        dataIndex: "name",
        key: "name",
        ellipsis: true,
        render: (v: any) => v || "-",
      },
      {
        title: t("Common-LastName"),
        dataIndex: "lastName",
        key: "lastName",
        ellipsis: true,
        responsive: ["md" as any],
        render: (v: any) => v || "-",
      },
      {
        title: t("Common-Email"),
        dataIndex: "email",
        key: "email",
        ellipsis: true,
        responsive: ["md" as any],
        render: (v: any) => v || "-",
      },
      {
        title: t("Common-Phone"),
        key: "phone",
        responsive: ["lg" as any],
        render: (_: any, record: any) =>
          record.mobile || record.phone || "-",
      },
    ],
    [t, canReadClients, location.pathname]
  );

  const clientsTabLabel =
    campaign?.clientCount != null
      ? `${t("Common-Clients")} (${campaign.clientCount})`
      : t("Common-Clients");

  if (loading && !campaign) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div>
            <Skeleton.Input style={{ width: 220, height: 20 }} active />
          </div>
          <div className={styles.actionButtonsWrapper}>
            <Skeleton.Button style={{ width: 80, height: 32 }} active />
            <Skeleton.Button style={{ width: 80, height: 32 }} active />
          </div>
        </div>
        <div className={styles.contentWrapper}>
          <div className={styles.campaignInfoWrapper}>
            <Skeleton active paragraph={{ rows: 12 }} />
          </div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return <NotFound />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxMagicWand />,
                label: t("NavBar-Campaigns"),
                path: getBasePath(),
                search: location.search,
              },
              {
                label: campaign?.name || t("Common-Campaign"),
                isCurrentPage: true,
              },
            ]}
          />
        </div>
        <div className={styles.actionButtonsWrapper}>
          {canEdit && (
            <>
              <Button
                onClick={handleEdit}
                className={styles.saveButton}
                type="primary"
                loading={saving}
                disabled={isCreating}
              >
                {t("Common-Save")}
              </Button>
              <Button
                onClick={handleCancelButton}
                className={styles.cancelButton}
                type="primary"
                disabled={saving}
              >
                {t("Common-Cancel")}
              </Button>
            </>
          )}
        </div>
      </div>
      <div className={styles.contentWrapper}>
        {isCreating && (
          <Alert
            type="info"
            showIcon
            message={t("Common-CampaignCreatingAlert")}
            style={{ marginBottom: 16 }}
          />
        )}
        <div className={styles.campaignInfoWrapper}>
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            className={styles.tabs}
          >
            <TabPane tab={t("Common-BasicInformation")} key="detail">
              <Form
                name="editCampaign"
                layout="vertical"
                form={editForm}
                autoComplete="off"
                className={styles.editForm}
              >
                <div className={styles.sectionTitle}>
                  {t("Common-BasicInformation")}
                </div>
                <div className={styles.itemWrapper}>
                  <Form.Item
                    className={styles.formItem}
                    label={t("Common-Name")}
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: t("Common-NameNotEntered"),
                      },
                    ]}
                  >
                    <Input placeholder={t("Common-Name")} />
                  </Form.Item>
                  <Form.Item
                    className={styles.formItem}
                    label={t("Common-Description")}
                    name="description"
                  >
                    <Input.TextArea
                      placeholder={t("Common-Description")}
                      rows={3}
                    />
                  </Form.Item>
                </div>
                <div className={styles.itemWrapper}>
                  <Form.Item
                    className={styles.formItem}
                    label={t("Common-Validity")}
                    name="validity"
                  >
                    <RangePicker
                      className={styles.dateSelector}
                      placeholder={[t("Common-ValidFrom"), t("Common-ValidTo")]}
                    />
                  </Form.Item>
                </div>
                <div className={styles.sectionTitle}>{t("Common-Metrics")}</div>
                <div className={styles.itemWrapper}>
                  <Form.Item
                    className={styles.formItem}
                    label={t("Common-Type")}
                    name="campaignType"
                  >
                    <Select allowClear placeholder={t("Common-Type")}>
                      <Select.Option value="INCENTIVE">
                        {t("Common-CampaignTypeIncentive")}
                      </Select.Option>
                      <Select.Option value="PROMOTION">
                        {t("Common-CampaignTypePromotion")}
                      </Select.Option>
                      <Select.Option value="ACTIVATION">
                        {t("Common-CampaignTypeActivation")}
                      </Select.Option>
                      <Select.Option value="SEGMENT">
                        {t("Common-CampaignTypeSegment")}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    className={styles.formItem}
                    label={t("Common-Status")}
                    name="campaignStatus"
                  >
                    <Select allowClear placeholder={t("Common-Status")}>
                      <Select.Option value="FUTURA">
                        {t("Common-CampaignStatusFuture")}
                      </Select.Option>
                      <Select.Option value="ACTIVA">
                        {t("Common-CampaignStatusActive")}
                      </Select.Option>
                      <Select.Option value="FINALIZADA">
                        {t("Common-CampaignStatusFinished")}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </div>
                <div className={styles.itemWrapper}>
                  <Form.Item
                    className={styles.formItem}
                    label={t("Common-EstimatedImpact")}
                    name="estimatedImpact"
                  >
                    <InputNumber
                      className={styles.inputNumber}
                      min={0}
                      step={0.01}
                      placeholder={t("Common-EstimatedImpact")}
                    />
                  </Form.Item>
                  <Form.Item
                    className={styles.formItem}
                    label={t("Common-EstimatedCost")}
                    name="estimatedCost"
                  >
                    <InputNumber
                      className={styles.inputNumber}
                      min={0}
                      step={0.01}
                      placeholder={t("Common-EstimatedCost")}
                    />
                  </Form.Item>
                </div>
                <div className={styles.itemWrapper}>
                  <Form.Item
                    className={styles.formItem}
                    label={t("Common-EstimatedROI")}
                    name="estimatedRoi"
                  >
                    <InputNumber
                      className={styles.inputNumber}
                      step={0.01}
                      placeholder={t("Common-EstimatedROI")}
                    />
                  </Form.Item>
                  <Form.Item
                    className={styles.formItem}
                    label={t("Common-EstimatedAudience")}
                    name="estimatedAudience"
                  >
                    <InputNumber
                      className={styles.inputNumber}
                      min={0}
                      step={1}
                      placeholder={t("Common-EstimatedAudience")}
                    />
                  </Form.Item>
                </div>
                <div className={styles.itemWrapper}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>
                      {t("Common-ConversionPercentage")}:
                    </span>
                    <span className={styles.infoValue}>
                      {campaign?.conversionPercentage != null
                        ? `${campaign.conversionPercentage.toFixed(2)}%`
                        : "-"}
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>
                      {t("Common-IncrementalSales")}:
                    </span>
                    <span className={styles.infoValue}>
                      {campaign?.incrementalSales != null
                        ? `$${campaign.incrementalSales.toFixed(2)}`
                        : "-"}
                    </span>
                  </div>
                </div>
                <div className={styles.itemWrapper}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>
                      {t("Common-Cost")}:
                    </span>
                    <span className={styles.infoValue}>
                      {!campaign?.reward
                        ? t("Common-NotApplicableNoIncentive")
                        : campaign?.cost != null
                        ? `$${campaign.cost.toFixed(2)}`
                        : "-"}
                    </span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>
                      {t("Common-ROI")}:
                    </span>
                    <span className={styles.infoValue}>
                      {!campaign?.reward
                        ? t("Common-NotApplicableNoIncentive")
                        : campaign?.roi != null
                        ? `${campaign.roi.toFixed(2)}%`
                        : "-"}
                    </span>
                  </div>
                </div>
                <div className={styles.sectionTitle}>
                  {t("Common-Associations")}
                </div>
                <div className={styles.itemWrapper}>
                  <Form.Item
                    className={styles.formItem}
                    label={t("Common-Promotion")}
                    name="promotionId"
                  >
                    <Select
                      allowClear
                      showSearch
                      filterOption={false}
                      onSearch={handleSearchPromotions}
                      onChange={handlePromotionChange}
                      placeholder={t("Common-SearchByName")}
                    >
                      {getOptions(promotions)}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    className={styles.formItem}
                    label={t("Common-Reward")}
                    name="rewardId"
                  >
                    <Select
                      allowClear
                      showSearch
                      filterOption={false}
                      onSearch={handleSearchRewards}
                      onChange={handleRewardChange}
                      placeholder={t("Common-SearchByName")}
                    >
                      {getOptions(rewards)}
                    </Select>
                  </Form.Item>
                </div>
                <div className={styles.itemWrapper}>
                  <Form.Item
                    className={styles.formItem}
                    label={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{t("Common-ClientSegments")}</span>
                        <Button
                          type="link"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={handleCreateNewSegment}
                          style={{ padding: 0, height: "auto" }}
                        >
                          {t("Common-NewClientSegment")}
                        </Button>
                      </div>
                    }
                    name="clientSegmentIds"
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      showSearch
                      loading={segmentsLoading}
                      filterOption={(input: string, option: any) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      onSearch={handleSearchClientSegments}
                      placeholder={t("Common-SearchByName")}
                    >
                      {getOptions(clientSegments)}
                    </Select>
                  </Form.Item>
                </div>
                <div className={styles.sectionTitle}>
                  {t("Common-AdditionalInformation")}
                </div>
                <div className={styles.itemWrapper}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>
                      {t("Common-CreatedDate")}:
                    </span>
                    <span className={styles.infoValue}>
                      {campaign?.createdAt
                        ? moment(campaign.createdAt).format("DD-MM-YYYY HH:mm")
                        : "-"}
                    </span>
                  </div>
                </div>
              </Form>
            </TabPane>
            <TabPane tab={clientsTabLabel} key="clients">
              <div className={styles.filtersWrapper}>
                <div className={styles.filterWrapper}>
                  <Input
                    className={styles.filterInput}
                    placeholder={t("Common-Name")}
                    value={nameFilterValue}
                    onChange={(e) => setNameFilterValue(e.target.value)}
                    allowClear
                  />
                </div>
                <div className={styles.filterWrapper}>
                  <Input
                    className={styles.filterInput}
                    placeholder={t("Common-Email")}
                    value={emailFilterValue}
                    onChange={(e) => setEmailFilterValue(e.target.value)}
                    allowClear
                  />
                </div>
                <div className={styles.filterWrapper}>
                  <Input
                    className={styles.filterInput}
                    placeholder={t("Common-Phone")}
                    value={phoneFilterValue}
                    onChange={(e) => setPhoneFilterValue(e.target.value)}
                    allowClear
                  />
                </div>
                <div className={styles.filterWrapper}>
                  <Input
                    className={styles.filterInput}
                    placeholder={t("Common-DocumentNumber")}
                    value={idNumberFilterValue}
                    onChange={(e) => setIdNumberFilterValue(e.target.value)}
                    allowClear
                  />
                </div>
                <div className={styles.filterClearActions}>
                  <Button
                    className={styles.clearButton}
                    disabled={!hasActiveClientFilters()}
                    onClick={handleClearClientFilters}
                  >
                    {t("Common-Clear")}
                  </Button>
                </div>
              </div>
              {clientsLoading ? (
                <CampaignClientsTableSkeleton rows={clientsPageSize} />
              ) : clients.length === 0 ? (
                <Empty description={t("ActivationAction-NoClients")} />
              ) : (
                <Table
                  rowKey="id"
                  columns={clientColumns}
                  dataSource={clients}
                  pagination={getTablePagination(t, {
                    current: clientsPage,
                    total: clientsTotal,
                    pageSize: clientsPageSize,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                      setClientsPage(page);
                      setClientsPageSize(pageSize);
                    },
                  })}
                />
              )}
            </TabPane>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ViewCampaign;
